import React, { useMemo, useEffect, useState } from 'react'

import { SuggestionType } from 'src/types/search'

import { SuggesterBorderLessWrapper } from './styles'
import Input from 'src/comps/Atom/Inputs/Input/Input'
import ButtonReset from 'src/comps/ButtonReset'
import debounce from 'lodash/debounce'
import IconLocation from 'client/icons/Location'
import { useIntl } from 'react-intl'
import { isGeo, isProperty } from 'src/context/search/utils'
import { ErrorMessage } from '../../ErrorMessage'

interface ISuggesterBorderLessProps {
  suggestion?: SuggestionType | null
  onChangeInput: (value: string) => void
  onClear: () => void
  hasFocus?: boolean
  hasError?: boolean
  suggesterSearch?: string
  onFocus?: () => void
}

export function SuggesterBorderLess({
  suggestion,
  onFocus,
  onClear,
  onChangeInput,
  hasFocus = false,
  hasError = false,
  suggesterSearch = '',
}: ISuggesterBorderLessProps) {
  const intl = useIntl()
  const placeholderText = intl.formatMessage({ id: 'search.placeholder' })
  const mapAreaText = intl.formatMessage({ id: 'global.search-map-area' })
  const errorMessage = intl.formatMessage({ id: 'global.enter-destination' })

  const isGeoSuggestion = isGeo(suggestion?.Type ?? '')
  const initialSearchTerm = isGeoSuggestion
    ? mapAreaText
    : (suggestion?.Description ?? suggesterSearch)

  const [searchTerm, setSearchTerm] = useState(initialSearchTerm)

  const suggestionText = isProperty(suggestion?.Type) ? suggestion?.Text : suggestion?.Description

  useEffect(() => {
    setSearchTerm(isGeoSuggestion ? mapAreaText : (suggestionText ?? ''))
  }, [suggestionText, mapAreaText, isGeoSuggestion])

  const externalOnChangeInput = useMemo(
    () =>
      debounce(
        (data: string) => {
          onChangeInput(data)
        },
        300,
        {
          leading: false,
          trailing: true,
        }
      ),
    [onChangeInput]
  )

  useEffect(() => {
    return () => {
      externalOnChangeInput.cancel?.()
    }
  }, [externalOnChangeInput])

  function handleChangeInput(text: string) {
    setSearchTerm(text)
    externalOnChangeInput(text)
  }

  function handleResetInput(
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) {
    e.stopPropagation()
    setSearchTerm('')
    externalOnChangeInput('')
    onClear()
  }

  return (
    <SuggesterBorderLessWrapper
      hasFocus={hasFocus}
      hasError={hasError}
      data-testid="suggester-borderless"
      onFocus={onFocus}
    >
      <Input
        label=""
        icon={<IconLocation size={24} />}
        onChange={handleChangeInput}
        value={searchTerm}
        placeholder={placeholderText}
      />
      {hasError && <ErrorMessage errorMessage={errorMessage} />}
      <ButtonReset handleClick={handleResetInput} visible={searchTerm?.length > 0} />
    </SuggesterBorderLessWrapper>
  )
}
